import {
  Box,
  Button,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Text,
  useTab,
  VStack,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import UpgradeToWaveEssentialEmptyState from '~components/organization/UpgradeToWaveEssentialModal/EmptyState';
import { isWaveSubscription } from '~utils/subscriptions';
import { isDefined } from '~utils/types';
import { CustomerSubscriptionsTableSubscriptionFragment } from '../CustomerSubscriptionsTable/__generated__/CustomerSubscriptionsTable.graphql';
import { CustomerSubscriptionsTable } from './CustomerSubscriptionsTable';
import { useCustomerSubscriptionsTable } from './CustomerSubscriptionsTable/useCustomerSubscriptionsTable';

interface SubscriptionsTabProps {
  title: string;
  subtitle?: string;
  showBadge?: boolean;
}

interface CustomerSubscriptionsProps {
  currentSubscriptionIds: string[];
  subscriptions: CustomerSubscriptionsTableSubscriptionFragment[];
  displayCount: number;
  onChange: (subscriptionIds: string[]) => void;
}

enum TabIndex {
  Lite = 0,
  Essential = 1,
}

export const SubscriptionsTab = forwardRef<HTMLElement, SubscriptionsTabProps>(
  ({ title, subtitle, showBadge = false, ...props }, ref) => {
    const tabProps = useTab({ ...props, ref });
    const isSelected = !!tabProps['aria-selected'];
    const hasSubtitle = isDefined(subtitle) && subtitle !== '';

    return (
      <Button
        variant="unstyled"
        height="auto"
        fontWeight="inherit"
        position="relative"
        padding="6 4"
        backgroundColor="white"
        borderBottom="3px solid"
        borderBottomColor={isSelected ? 'blue.500' : 'transparent'}
        display="flex"
        whiteSpace="pre-wrap"
        textAlign="left"
        {...(hasSubtitle
          ? {
              flexDirection: 'column',
              gap: '1',
              justifyContent: 'center',
              alignItems: 'flex-start',
            }
          : {
              justifyContent: 'space-between',
            })}
        {...tabProps}
      >
        <strong>{title}</strong>
        {showBadge && (
          <Tag
            variant="solid"
            background="gray.50"
            color="blue.800"
            borderRadius="full"
            position={hasSubtitle ? 'absolute' : 'static'}
            top="2"
            right="2"
          >
            {t('currentPlan')}
          </Tag>
        )}
        {hasSubtitle && <span>{subtitle}</span>}
      </Button>
    );
  },
);

export function CustomerSubscriptions({
  currentSubscriptionIds = [],
  subscriptions,
  displayCount,
  onChange,
}: CustomerSubscriptionsProps) {
  const [hideExpired, setHideExpired] = useState(false);

  const existingSubscriptions = subscriptions.filter(({ id }) =>
    currentSubscriptionIds.includes(id),
  );

  const hasEssentialSubscription =
    existingSubscriptions.length > 0 && existingSubscriptions.every(isWaveSubscription);

  const [tabIndex, setTabIndex] = useState(
    hasEssentialSubscription ? TabIndex.Essential : TabIndex.Lite,
  );
  const hasUserSelectedTabRef = useRef(false);

  // Track whether we are on the Lite tab to block table selection callbacks
  const isLiteTabRef = useRef(tabIndex === TabIndex.Lite);

  const table = useCustomerSubscriptionsTable({
    data: subscriptions,
    displayCount,
    hideExpired,
    currentSubscriptionIds,
  });

  const handleTabsChange = (index: TabIndex) => {
    hasUserSelectedTabRef.current = true;

    isLiteTabRef.current = index === TabIndex.Lite;

    if (index === TabIndex.Lite) {
      onChange([]);
    } else {
      // On switch to Essential: restore existing selections (or empty for new customers).
      // The table's useEffect will fire and push actual selections back via handleSelectionChange.
      onChange(currentSubscriptionIds);
    }

    setTabIndex(index);
  };

  // Sync tab index when the server-driven hasEssentialSubscription changes,
  // but do NOT call onChange here — the form already holds the correct value.
  useEffect(() => {
    if (hasUserSelectedTabRef.current) return;

    const newIndex = hasEssentialSubscription ? TabIndex.Essential : TabIndex.Lite;
    isLiteTabRef.current = newIndex === TabIndex.Lite;
    setTabIndex(newIndex);
  }, [hasEssentialSubscription]);
  // Only forward table selection changes when on the Essential tab
  const handleSelectionChange = (selectedIds: string[]) => {
    if (!isLiteTabRef.current) {
      onChange(selectedIds);
    }
  };

  return (
    <Tabs variant="unstyled" index={tabIndex} onChange={handleTabsChange}>
      <TabList display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap="1" mb="10">
        <SubscriptionsTab title="Lite Plan" showBadge={tabIndex === TabIndex.Lite} />
        <SubscriptionsTab title="Essential Plan" showBadge={tabIndex === TabIndex.Essential} />
      </TabList>
      <TabPanels>
        <TabPanel></TabPanel>
        <TabPanel padding="0">
          {subscriptions.length > 0 ? (
            <VStack spacing="6" alignItems="stretch">
              <Text color="gray.500">
                {t('selectSubscriptionText')}{' '}
                <strong>
                  {displayCount} {displayCount === 1 ? 'display' : 'displays'}
                </strong>{' '}
                <Trans i18nKey="essentialPlanMessage" components={{ strong: <strong /> }} />
              </Text>
              <CustomerSubscriptionsTable
                table={table}
                hideExpired={hideExpired}
                onHideExpiredChange={setHideExpired}
                onSelectionChange={handleSelectionChange}
              />
            </VStack>
          ) : (
            <Box
              boxShadow="elevated"
              border="1px solid"
              borderRadius="md"
              borderColor="gray.100"
              overflowX="auto"
              background="white"
              paddingX="10"
            >
              <UpgradeToWaveEssentialEmptyState noSubscriptions />
            </Box>
          )}
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
}
